import type {
  ProjectItem,
  ExperienceItem,
  WritingPost,
  SocialItem,
  MetaRow,
  EssayData,
} from "../types";
import essayContent from "./essayContent";

const heroPhoto = new URL("../assets/hero-2.jpeg", import.meta.url).href;
const portrait = new URL("../assets/hero.png", import.meta.url).href;
const travelPhoto = new URL("../assets/travel.jpg", import.meta.url).href;

const yearsExperience = new Date().getFullYear() - 2016;

export const hero = {
  kicker: "Engineer by trade · Wanderer by nature",
  headline: ["I build ", "products", "and collect", "horizons."] as const,
  lead: "A decade building software. A lifetime exploring. This is a collection of the things I've built, the places I've wandered and the ideas I've picked up along the way.",
  photo: heroPhoto,
  photoTag: "Field Notes № 14",
  photoPlace: "Hội An",
  photoCoord: "Vietnam · 15°N",
};

export const about = {
  greeting: "Hi there 👋🏻, I'm Megholova.",
  body: `I'm a software engineer with more than a decade of experience building products across the stack. From frontend applications and backend services to cloud infrastructure and modern AI systems, I enjoy turning ideas into reliable software that people actually use.

Over the years, I've worked with startups, experienced an acquisition, contributed to products serving millions of users, and helped build systems from the ground up. Today, my toolkit includes React, TypeScript, Go, PostgreSQL, AWS, and an increasing focus on AI-powered applications.

Outside of work, I'm a relentless wanderer driven by an insatiable curiosity to discover the world beyond screens. Whether it's a new technology, a new place, or a new perspective, I'm always looking for what lies just beyond the familiar.`,
  portrait: portrait,
  meta: [
    { label: "Experience", value: `${yearsExperience} years` },
    { label: "Building", value: "Products · Platforms · AI" },
    { label: "Currently", value: "Open to a good conversation" },
  ] as MetaRow[],
};

export const experience: {
  title: string;
  subTitle: string;
  items: ExperienceItem[];
} = {
  title: "A decade of shipping.",
  subTitle:
    "A timeline of products built, problems solved and lessons learned.",
  items: [
    {
      period: "2024 — Now",
      role: "Senior Technical Architect",
      org: "EMQM Group",
      note: "Startup life found me again through a chance conversation with my former CEO from Agara. As the founding engineer, I've been fortunate to help shape a company from its earliest days and see ideas evolve into something real.",
    },
    {
      period: "2023 — 2024",
      role: "Senior Software Engineer",
      org: "LegalZoom",
      note: "Worked on products used by millions of customers and became increasingly drawn to the intersection of engineering and product. This was where customer impact became as important as technical execution.",
    },
    {
      period: "2021 — 2023",
      role: "Software Engineer",
      org: "Coinbase",
      note: "Joined through Agara's acquisition and learned how large organizations create impact through communication, documentation and collaboration at scale.",
    },
    {
      period: "2019 — 2021",
      role: "Lead Software Engineer",
      org: "Agara",
      note: "Employee #28. What began as a frontend engineering role became my first leadership opportunity, culminating in the SaaS platform that defined the startup's next chapter.",
    },
    {
      period: "2018 — 2019",
      role: "Software Engineer",
      org: "Baker Hughes",
      note: "My first experience inside a global enterprise, where architecture reviews, planning and process became as important as writing code.",
    },
    {
      period: "2016 — 2018",
      role: "Software Engineer",
      org: "Meredith Corp.",
      note: "Fresh out of university and newly arrived in Bangalore, I discovered frontend engineering and the joy of constantly figuring things out.",
    },
  ],
};

export const projects: {
  title: string;
  description: string;
  items: ProjectItem[];
} = {
  title: "Side Quests",
  description:
    "A collection of experiments, tools, and projects from different chapters of my journey. Some are works in progress, others are relics of past curiosities. Together, they chart the technologies I've explored, the problems I've enjoyed solving, and the ideas that refused to leave me alone.",
  items: [
    {
      id: "binge",
      name: "Binge Food Fun",
      tagline: "Your BFF food app",
      link: "https://binge-food-fun.netlify.app/",
      snapshot:
        "https://d33wubrfki0l68.cloudfront.net/66c05c4f64889d0008adfe22/screenshot_2024-08-17-08-16-49-0000.webp",
      description:
        "A food-ordering app mock with a discovery view, restaurant menu pages and a working cart.",
      tech: ["React.js", "TailwindCSS", "JavaScript"],
      source: "https://github.com/megholova91/food-app",
    },
    {
      id: "puzzles",
      name: "Vanilla JS Puzzles",
      tagline: "Games & UI, no framework",
      link: "https://js-puzzles.netlify.app/",
      snapshot:
        "https://d33wubrfki0l68.cloudfront.net/664267eda8fdab0008721b4f/screenshot_2024-05-13-19-20-36-0000.webp",
      description:
        "Fun games and UI components built with nothing but HTML, CSS and vanilla JavaScript.",
      tech: ["HTML", "CSS", "JavaScript"],
      source: "https://github.com/megholova91/vanilla-js-puzzles",
    },
    {
      id: "todo",
      name: "To-do App",
      tagline: "Tick, done, repeat",
      link: "https://megholova-to-do-app.netlify.app/",
      snapshot:
        "https://d33wubrfki0l68.cloudfront.net/6469080c7f935b0008d1cddd/screenshot_2023-05-20-17-49-11-0000.png",
      description:
        "Add items, mark them done, and they glide to a Done section you can pull back from. A study in tiny, satisfying state.",
      tech: ["HTML", "CSS", "JavaScript"],
      source: "https://github.com/megholova91/To-Do-App",
    },
    {
      id: "doctor",
      name: "Doctor's Appointment Booking",
      tagline: "Pick a doctor, pick a slot",
      link: "https://megholova-doctor-appointment-app.netlify.app/",
      snapshot:
        "https://d33wubrfki0l68.cloudfront.net/64690a28e3ef8f00080152c9/screenshot_2023-05-20-17-58-51-0000.png",
      description:
        "Browse doctors, view available slots, and book by name — a clean multi-step flow.",
      tech: ["React.js", "TypeScript", "SCSS", "JavaScript"],
      source: "https://github.com/megholova91/Doctor-appointment-booking-app",
    },
  ],
};

export const travel = {
  placeholder: true,
  kicker: "Field Notes",
  headline: "The map is still being drawn.",
  lead: "Every picture and anecdote here captures the awe of the world beyond the screen — from ancient streets to natural wonders.",
  image: travelPhoto,
  chips: [
    "Hội An, Vietnam",
    "Antarctic Sound",
    "Himalayas, India",
    "more soon…",
  ],
};

// Travel home intro. Article cards come from src/data/travel/.
export const travelPage = {
  intro:
    "A growing journal of the places that pulled me away from the screen — short stories and photographs from the road.",
  note: "more on the way",
};

export const writing: {
  placeholder: boolean;
  lead: string;
  handle: string;
  link: string;
  posts: WritingPost[];
} = {
  placeholder: false,
  lead: "I occasionally write about building for the web, the craft of frontend, and lessons from the road.",
  handle: "@megholova",
  link: "https://medium.com/@megholova",
  posts: [
    {
      title: "Part 1: AI for Everyone — An easy introduction",
      meta: "AI · June 2026",
      link: "https://medium.com/@megholova/part-1-ai-for-everyone-an-easy-introduction-2b84030b92ea",
    },
    {
      title: "Part 2: AI for Everyone — How to talk to AI",
      meta: "AI · June 2026",
      link: "https://medium.com/@megholova/part-2-ai-for-everyone-how-to-talk-to-ai-a448fdafa813",
    },
  ],
};

export const socials: SocialItem[] = [
  {
    type: "Email",
    link: "mailto:megholova@gmail.com",
    handle: "megholova@gmail.com",
  },
  {
    type: "LinkedIn",
    link: "https://www.linkedin.com/in/megholova/",
    handle: "in/megholova",
  },
  {
    type: "GitHub",
    link: "https://github.com/megholova91",
    handle: "megholova91",
  },
  {
    type: "Medium",
    link: "https://medium.com/@megholova",
    handle: "@megholova",
  },
  {
    type: "Instagram",
    link: "https://www.instagram.com/megholova/",
    handle: "@megholova",
  },
];

export const SITE_NAME = "Megholova";
export const SITE_ROLE = "Software Engineer";

export const essay: EssayData = essayContent;
